"use client";

import axiosInstance from "@/app/api";
import {useNeoContext} from "@/context/neo/neo.context";
import {GPSTrackerStatus} from "@/libs/enums/live";
import {convertMTOKM} from "@/libs/helper";
import {formatCellValue} from "@/libs/helper/formatCellValue";
import {driversService} from "@/service/drivers.service";
import {reortsService} from "@/service/reports.service";
import {activeRoute} from "@/service/routePlan.service";
import {Image} from "@nextui-org/react";
import {useEffect, useState} from "react";
import {
  FiBell,
  FiClock,
  FiDroplet,
  FiMapPin,
  FiNavigation,
  FiShield,
  FiUser,
  FiUsers
} from "react-icons/fi";
import {MdDirectionsBus, MdLocalGasStation} from "react-icons/md";
import {RiRouteLine} from "react-icons/ri";
import {SlArrowRight} from "react-icons/sl";
import {TbRoute} from "react-icons/tb";

type InsightTab =
  | "trip"
  | "alerts"
  | "attendance"
  | "fuel"
  | "driver"
  | "fleet";

interface VehicleInsightsPanelProps {
  isOpen?: boolean;
  onToggle?: () => void;
  vehicle?: TrackingInfo | null;
}

function normalizeVehicleKey(value: any) {
  return String(value ?? "")
    .replace(/[\s_-]+/g, "")
    .toUpperCase();
}

function vehicleNamesMatch(a: any, b: any) {
  const left = normalizeVehicleKey(a);
  const right = normalizeVehicleKey(b);
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
}

function getAvailability(vehicle?: TrackingInfo | null) {
  if (!vehicle) return "Available now";
  switch (vehicle.gpsTrackerStatus) {
    case GPSTrackerStatus.STATIONARY:
      return "Available now";
    case GPSTrackerStatus.MOVING:
      return "On trip";
    case GPSTrackerStatus.OFFLINE:
      return "Offline";
    case GPSTrackerStatus.DEAD:
      return "Unavailable";
    default:
      return "Available now";
  }
}

const DUMMY_DATA = {
  vehicleNumber: "TN 37 EC 7815",
  routeName: "Route 26",
  routeFrom: "Villankurichi",
  routeTo: "Airport",
  institution: "NIET",
  isLive: true,
  kmToday: 44.42,
  currentSpeed: 0,
  vehicleType: "HPV",
  seats: 59,
  actualStart: "09:28 AM",
  estimatedEnd: "11:15 AM",
  runningTime: "1h 47m",
  distance: 44.42,
  startPoint: "Villankurichi",
  endPoint: "Coimbatore Airport",
  maxSpeed: 58,
  avgSpeed: 25,
  route: "R26_M_T01",
  timeline: [
    {
      type: "start",
      title: "Trip started",
      time: "09:28 AM",
      location: "Villankurichi",
      detail: null
    },
    {
      type: "overspeed",
      title: "Overspeed detected",
      time: "09:51 AM",
      location: "Nehu Road",
      detail: "68 kph"
    },
    {
      type: "checkpoint",
      title: "Checkpoint reached",
      time: "10:14 AM",
      location: "Nehru College",
      detail: null
    }
  ],
  alerts: [
    {
      type: "overspeed",
      title: "Overspeed — 68 kph in 40 kph zone",
      time: "09:51 AM",
      location: "Nehu Road"
    },
    {
      type: "deviation",
      title: "Route deviation — 320 m off path",
      time: "10:05 AM",
      location: "Sitra Junction"
    },
    {
      type: "idle",
      title: "Idle 12 min with engine on",
      time: "10:38 AM",
      location: "Broadway"
    },
    {
      type: "fuel",
      title: "Fuel drop — 4 L detected",
      time: "08:15 AM",
      location: "Depot"
    },
    {
      type: "insurance",
      title: "Insurance expiring in 18 days",
      time: "",
      location: "Renewal due: 03 Jul 2026"
    }
  ]
};

const TABS: {key: InsightTab; label: string; icon: React.ReactNode}[] = [
  {key: "trip", label: "Trip", icon: <TbRoute size={18} />},
  // {key: "alerts", label: "Alerts", icon: <FiBell size={18} />},
  {key: "attendance", label: "Attendance", icon: <FiUsers size={18} />},
  {key: "fuel", label: "Fuel", icon: <MdLocalGasStation size={18} />},
  {key: "driver", label: "Driver", icon: <FiUser size={18} />}
  // {key: "fleet", label: "Fleet", icon: <FiGrid size={18} />}
];

function TimelineIcon({type}: {type: string}) {
  if (type === "start") {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
          <path d="M8 5v14l11-7z" />
        </svg>
      </div>
    );
  }
  if (type === "overspeed") {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-400">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
      </div>
    );
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500">
      <FiMapPin size={14} color="white" />
    </div>
  );
}

const ALERT_TYPES: Record<number, {name: string; color: string}> = {
  1: {name: "Speed alert", color: "#2563EB"},
  2: {name: "Movement alert", color: "#0EA5E9"},
  3: {name: "Distance limit alert", color: "#0D9488"},
  4: {name: "Fence alert", color: "#16A34A"},
  5: {name: "Sudden acceleration alert", color: "#FC2A3D"},
  6: {name: "Harsh braking alert", color: "#F28A03"},
  7: {name: "Battery unplug alert", color: "#B91C1C"},
  8: {name: "Over speed alert", color: "#6366F1"},
  9: {name: "Idle alert", color: "#CA8A04"},
  10: {name: "Working Hours Breach", color: "#9333EA"},
  11: {name: "Trip driven", color: "#0891B2"},
  12: {name: "SOS alert", color: "#DC2626"},
  13: {name: "Vehicle compliance", color: "#475569"},
  14: {name: "Driver compliance", color: "#64748B"},
  15: {name: "Driver no swipe", color: "#EA580C"},
  16: {name: "Non-trip Movement", color: "#7C3AED"},
  17: {name: "Continuous driving alert", color: "#DB2777"},
  18: {name: "Un-defined time driving alert", color: "#A21CAF"},
  19: {name: "Trip starting alert", color: "#059669"},
  20: {name: "Trip ending alert", color: "#047857"},
  21: {name: "Near home alert", color: "#65A30D"},
  22: {name: "Fail safe alert", color: "#E11D48"},
  23: {name: "Onward pickup alert", color: "#1D4ED8"},
  24: {name: "Onward drop alert", color: "#4338CA"},
  25: {name: "Return pickup alert", color: "#0E7490"},
  26: {name: "Return drop alert", color: "#115E59"},
  27: {name: "Sos alert", color: "#DC2626"},
  28: {name: "Harsh cornering alert", color: "#D32E9F"}
};
const DEFAULT_ALERT_COLOR = "#64748B";

function formatAlertTime(value: any) {
  if (!value) return "";
  const date = new Date(Number(value));
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
}

function isPickupTrip(tripType: any) {
  const normalized = String(tripType ?? "").toLowerCase();
  return tripType === 0 || normalized === "pickup" || normalized === "onward";
}

function tripTypeLabel(tripType: any) {
  return isPickupTrip(tripType) ? "Pickup Trip" : "Drop Trip";
}

function formatTripTime(value: any) {
  if (value === undefined || value === null || value === "") return "--";
  if (typeof value === "number") {
    return formatCellValue.formatTimestamp(value, "Time");
  }
  if (typeof value === "string" && /^\d+$/.test(value)) {
    return formatCellValue.formatTimestamp(Number(value), "Time");
  }
  return value;
}

function formatTripDistance(value: any) {
  if (value === undefined || value === null || value === "") return "--";
  if (typeof value === "number") {
    return convertMTOKM(value);
  }
  if (typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    return convertMTOKM(Number(value));
  }
  return value;
}

function TripTab({vehicle}: {vehicle?: TrackingInfo | null}) {
  const [loading, setLoading] = useState(false);
  const [trips, setTrips] = useState<any[]>([]);

  useEffect(() => {
    if (!vehicle?.name) {
      setTrips([]);
      return;
    }
    setLoading(true);
    activeRoute
      .onGetActiveRoutes()
      .then((result: any) => {
        const rows = result?.rows ?? [];
        setTrips(
          rows.filter((row: any) =>
            vehicleNamesMatch(row.vehicleName, vehicle.name)
          )
        );
      })
      .catch(() => setTrips([]))
      .finally(() => setLoading(false));
  }, [vehicle?.name]);

  if (loading) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-gray-400">
        Loading...
      </div>
    );
  }

  if (!trips.length) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-gray-400">
        No trips available
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="mb-2 text-sm font-semibold uppercase tracking-wider">
          Trips
        </div>
        <div className="flex flex-col gap-2">
          {trips.map((trip: any, idx: number) => (
            <div
              key={idx}
              className="rounded-xl border border-blue-100 bg-blue-50 p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-bold text-gray-800">
                  {trip.routeName}
                </span>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    isPickupTrip(trip.tripType)
                      ? "bg-green-100 text-green-600"
                      : "bg-blue-100 text-blue-600"
                  }`}
                >
                  {tripTypeLabel(trip.tripType)}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400">Start</span>
                  <span className="text-xs font-semibold text-gray-700">
                    {formatTripTime(trip.startTime)}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400">Est. End</span>
                  <span className="text-xs font-semibold text-gray-700">
                    {formatTripTime(trip.endTime)}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400">Distance</span>
                  <span className="text-xs font-semibold text-gray-700">
                    {formatTripDistance(trip.totalDistance ?? trip.distance)}
                  </span>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
                <div className="flex items-center gap-1">
                  <FiUsers size={11} />
                  <span>{trip.stops ?? 0} stops scheduled</span>
                </div>
                {trip.users != null && <span>{trip.users} users</span>}
              </div>
              {trip.activeOnDays && (
                <div className="mt-1.5 text-[11px] text-gray-400">
                  Active on : {trip.activeOnDays}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AlertsTab({vehicle}: {vehicle?: TrackingInfo | null}) {
  const {vehicles} = useNeoContext();
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState<any[]>([]);

  useEffect(() => {
    const trackerId = (vehicles ?? []).find(v =>
      vehicleNamesMatch(v.trackerName, vehicle?.name)
    )?.trackerId;
    if (!trackerId) {
      setAlerts([]);
      return;
    }
    // Ignore responses from a previous run (vehicle changed or unmounted)
    let cancelled = false;
    setLoading(true);
    axiosInstance()
      .post("/api/reports/fleet/alerts", {
        from: new Date().setHours(0, 0, 0, 0),
        to: new Date().setHours(23, 59, 59, 999),
        trackerId: [trackerId]
      })
      .then(response => {
        if (cancelled) return;
        setAlerts(
          response?.data?.status === "success"
            ? (response?.data?.data?.reportData ?? [])
            : []
        );
      })
      .catch(() => !cancelled && setAlerts([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [vehicle?.name, vehicles]);

  if (loading) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-gray-400">
        Loading...
      </div>
    );
  }
  if (!alerts.length) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-gray-500">
        No alerts today
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {alerts.map((alert, idx) => {
        const type = ALERT_TYPES[alert.alertType];
        const color = type?.color ?? DEFAULT_ALERT_COLOR;
        const title = type?.name ?? alert.alertTypeName ?? "Alert";
        const details = [
          formatAlertTime(alert.time),
          alert.driverName,
          alert.routeName
        ].filter(Boolean);
        return (
          <div
            key={alert.id ?? idx}
            className="flex items-start gap-3 rounded-xl border-l-4 p-3"
            style={{borderLeftColor: color, backgroundColor: `${color}14`}}
          >
            <div className="mt-0.5 flex-shrink-0" style={{color}}>
              <FiBell size={16} />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-800">
                {title}
              </span>
              {alert.message && (
                <span className="text-xs text-gray-500">{alert.message}</span>
              )}
              <span className="text-xs text-gray-800">
                {details.join(" · ")}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

type VehicleAttendanceUser = {
  userId: string;
  name: string;
  present: boolean;
};

type VehicleAttendance = {
  assignedUsers: number;
  presentUsers: number;
  absentUsers: number;
  attendancePercentage: number;
  users: VehicleAttendanceUser[];
};

function AttendanceTab({vehicle}: {vehicle?: TrackingInfo | null}) {
  const {filterContext, offset, vehicles} = useNeoContext();
  const client = filterContext?.client;
  const [loading, setLoading] = useState(false);
  const [attendance, setAttendance] = useState<VehicleAttendance | null>(null);

  useEffect(() => {
    if (
      !vehicle?.name ||
      !client?.institutionUserId ||
      !client?.institutionId
    ) {
      setAttendance(null);
      return;
    }
    const trackerId = (vehicles ?? []).find(v =>
      vehicleNamesMatch(v.trackerName, vehicle.name)
    )?.trackerId;
    const fromDate = new Date();
    const toDate = new Date();
    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(23, 59, 59, 999);

    setLoading(true);
    axiosInstance()
      .post("/api/reports/vehicle/rfidSwipes/v2", {
        from: fromDate.getTime(),
        to: toDate.getTime(),
        offset,
        instituteId: client.institutionId,
        filters: {
          branch: [client.institutionUserId],
          trackers: trackerId ? [trackerId] : []
        }
      })
      .then(response => {
        if (response?.data?.status !== "success") {
          setAttendance(null);
          return;
        }
        const rows: any[] = response?.data?.data?.reportData ?? [];
        const hasSwipe = (row: any) =>
          [
            row.onwardPick,
            row.onwardDrop,
            row.returnPick,
            row.returnDrop,
            row.otherSwipes
          ].some(swipes => Array.isArray(swipes) && swipes.length > 0);
        const usersById = new Map<string, VehicleAttendanceUser>();
        rows.forEach((row, index) => {
          const userId = String(
            row.userId ??
              row.employeeCode ??
              row.admissionNo ??
              row.rfidNo ??
              row.name ??
              `row-${index}`
          );
          const present = hasSwipe(row);
          const existing = usersById.get(userId);
          usersById.set(userId, {
            userId,
            name: row.name || row.employeeCode || row.admissionNo || userId,
            present: present || !!existing?.present
          });
        });
        const users = Array.from(usersById.values());
        const presentUsers = users.filter(u => u.present).length;
        const assignedUsers = users.length;
        setAttendance({
          assignedUsers,
          presentUsers,
          absentUsers: assignedUsers - presentUsers,
          attendancePercentage:
            assignedUsers > 0
              ? Number(((presentUsers / assignedUsers) * 100).toFixed(2))
              : 0,
          users
        });
      })
      .catch(() => setAttendance(null))
      .finally(() => setLoading(false));
  }, [
    vehicle?.name,
    client?.institutionUserId,
    client?.institutionId,
    offset,
    vehicles
  ]);

  if (loading) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-gray-400">
        Loading...
      </div>
    );
  }

  if (!attendance) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-gray-400">
        No attendance data for today
      </div>
    );
  }

  const {
    assignedUsers,
    presentUsers,
    absentUsers,
    attendancePercentage,
    users
  } = attendance;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-1 text-center">
        <div>
          <div className="text-xl font-bold text-gray-800">{assignedUsers}</div>
          <div className="text-xs text-gray-500">Assigned</div>
        </div>
        <div>
          <div className="text-xl font-bold text-green-500">{presentUsers}</div>
          <div className="text-xs text-gray-500">Present</div>
        </div>
        <div>
          <div className="text-xl font-bold text-red-500">{absentUsers}</div>
          <div className="text-xs text-gray-500">Absent</div>
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
          <span>Attendance</span>
          <span className="font-semibold text-gray-700">
            {attendancePercentage.toFixed(2)}%
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-green-500 transition-all"
            style={{width: `${Math.min(attendancePercentage, 100)}%`}}
          />
        </div>
      </div>

      {users.length > 0 && (
        <div>
          <div className="mb-2 text-sm font-semibold uppercase tracking-wider">
            User List
          </div>
          <div className="flex max-h-64 flex-col divide-y divide-gray-100 overflow-y-auto pr-2">
            {users.map(user => (
              <div
                key={user.userId}
                className="flex items-center justify-between py-2.5"
              >
                <div className="flex items-center gap-2">
                  <FiUser size={14} className="text-gray-400" />
                  <span className="text-sm text-gray-800">{user.name}</span>
                </div>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    user.present
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-600"
                  }`}
                >
                  {user.present ? "Present" : "Absent"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatFuelValue(raw: any, unit: string) {
  if (raw === undefined || raw === null || raw === "") return "N/A";
  const num = Number(raw);
  return Number.isFinite(num) ? `${num} ${unit}` : "N/A";
}

function FuelTab({vehicle}: {vehicle?: TrackingInfo | null}) {
  const [loading, setLoading] = useState(false);
  const [fuelRow, setFuelRow] = useState<any | null>(null);

  useEffect(() => {
    if (!vehicle?.name) {
      setFuelRow(null);
      return;
    }
    const fromTs = new Date().setHours(0, 0, 0, 0);
    const toTs = new Date().setHours(23, 59, 59, 999);

    setLoading(true);
    reortsService
      .onGetFuelReportData("/api/reports/fuel/consolidate", {
        from: fromTs,
        to: toTs
      })
      .then((res: any) => {
        if (res?.data?.code !== 200) {
          setFuelRow(null);
          return;
        }
        const rows = res?.data?.data?.reportData ?? [];
        const matched = rows.find((row: any) =>
          vehicleNamesMatch(row.vehicle, vehicle.name)
        );
        setFuelRow(matched ?? null);
      })
      .catch(() => setFuelRow(null))
      .finally(() => setLoading(false));
  }, [vehicle?.name]);

  if (loading) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-gray-400">
        Loading...
      </div>
    );
  }

  const metersToKm = (raw: any) => {
    const num = Number(raw);
    return Number.isFinite(num) ? num / 1000 : null;
  };
  const distanceKm = fuelRow ? metersToKm(fuelRow.distance) : null;
  const mileageKm = fuelRow ? metersToKm(fuelRow.mileage) : null;
  const distance = distanceKm !== null ? `${distanceKm.toFixed(2)} km` : "N/A";
  const mileage = mileageKm !== null ? `${mileageKm.toFixed(2)} km/L` : "N/A";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col divide-y divide-gray-100">
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiDroplet size={14} />
            <span>Start fuel</span>
          </div>
          <span className="text-sm font-medium text-gray-800">
            {formatFuelValue(fuelRow?.startFuel, "L")}
          </span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiDroplet size={14} />
            <span>Fuel filled</span>
          </div>
          <span className="text-sm font-medium text-gray-800">
            {formatFuelValue(fuelRow?.fuelFill, "L")}
          </span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <MdLocalGasStation size={14} />
            <span>Fuel consumed</span>
          </div>
          <span className="text-sm font-medium text-gray-800">
            {formatFuelValue(fuelRow?.fuelConsumed, "L")}
          </span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <RiRouteLine size={14} />
            <span>Distance</span>
          </div>
          <span className="text-sm font-medium text-gray-800">{distance}</span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiNavigation size={14} />
            <span>Mileage</span>
          </div>
          <span className="text-sm font-medium text-gray-800">{mileage}</span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiShield size={14} />
            <span>Possible Fuel theft</span>
          </div>
          <span className="text-sm font-semibold text-red-500">
            {formatFuelValue(fuelRow?.fuelTheft, "L")}
          </span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiDroplet size={14} />
            <span>End fuel</span>
          </div>
          <span className="text-sm font-medium text-gray-800">
            {formatFuelValue(fuelRow?.endFuel, "L")}
          </span>
        </div>
      </div>
    </div>
  );
}

function DriverTab({vehicle}: {vehicle?: TrackingInfo | null}) {
  const {filterContext, offset} = useNeoContext();
  const client = filterContext?.client;
  const [driverRow, setDriverRow] = useState<any | null>(null);

  useEffect(() => {
    if (!vehicle?.name || !client?.institutionUserId) {
      setDriverRow(null);
      return;
    }
    const fromTs = new Date().setHours(0, 0, 0, 0);
    const toTs = new Date().setHours(23, 59, 59, 999);

    driversService
      .onGetDrivers("/api/drivers/list", {
        userId: client.institutionUserId,
        instituteId: client.institutionId,
        pageNumber: 1,
        itemsPerPage: 100000,
        searchBy: "",
        sortColumn: "",
        sortValue: "",
        filterColumn: [],
        filterValue: [],
        filterType: "1",
        staffClassId: [],
        offset,
        from: fromTs,
        to: toTs,
        filters: {
          branch: [client.institutionUserId],
          trackers: [],
          status: [],
          source: "0",
          userType: "1",
          groupId: []
        }
      })
      .then((result: any) => {
        const rows = result?.rows ?? [];
        const matched = rows.find((row: any) =>
          vehicleNamesMatch(row.assignedTracker, vehicle.name)
        );
        setDriverRow(matched ?? null);
      })
      .catch(() => setDriverRow(null));
  }, [vehicle?.name, client?.institutionUserId, client?.institutionId, offset]);

  const name = driverRow?.name || vehicle?.driverName || "N/A";
  const phone = driverRow?.phone || vehicle?.driverNumber || "N/A";
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
        <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-full bg-gray-200">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={driverRow?.image || "/images/avatar.png"}
            alt={name}
            className="h-full w-full object-cover"
            onError={event => {
              event.currentTarget.src = "/images/avatar.png";
            }}
          />
        </div>
        <div className="flex flex-1 flex-col">
          <span className="text-sm font-bold text-gray-900">{name}</span>
          <span className="text-xs text-gray-500">{phone}</span>
        </div>
      </div>

      <div className="flex flex-col divide-y divide-gray-100">
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiShield size={14} />
            <span>Driver ID</span>
          </div>
          <span className="text-xs font-medium text-gray-800">
            {driverRow?.driverId || "N/A"}
          </span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiUser size={14} />
            <span>Operator</span>
          </div>
          <span className="text-xs font-medium text-gray-800">
            {driverRow?.operator || "N/A"}
          </span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiNavigation size={14} />
            <span>RFID No</span>
          </div>
          <span className="text-xs font-medium text-gray-800">
            {driverRow?.rfidNo || "N/A"}
          </span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiShield size={14} />
            <span>License no.</span>
          </div>
          <span className="text-xs font-medium text-gray-800">
            {driverRow?.licenceNo || "N/A"}
          </span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiClock size={14} />
            <span>License valid</span>
          </div>
          <span className="text-xs font-medium text-gray-800">
            {driverRow?.licenceExpiryDt || "N/A"}
          </span>
        </div>
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <MdDirectionsBus size={14} />
            <span>Assigned vehicle</span>
          </div>
          <span className="text-xs font-medium text-gray-800">
            {driverRow?.assignedTracker || "N/A"}
          </span>
        </div>
      </div>

      <div>
        <div className="mb-2 text-sm font-bold uppercase tracking-wider ">
          Compliance
        </div>
        <div className="flex flex-col divide-y divide-gray-100">
          {[
            //{label: "Licence", value: driverRow?.licenceExpiryDt},
            {label: "Badge", value: driverRow?.badgeExpiryDate},
            {label: "Medical Test", value: driverRow?.medicalTestExpiryDate},
            {label: "PVC", value: driverRow?.pvcExpiryDate},
            {label: "DDT", value: driverRow?.ddtExpiryDate},
            {label: "BGV", value: driverRow?.bgvExpiryDate}
          ].map((item, idx) => (
            <div key={idx} className="flex items-center justify-between py-2.5">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <FiShield size={14} />
                <span>{item.label}</span>
              </div>
              <span className="text-xs font-medium text-gray-800">
                {item.value || "N/A"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlaceholderTab({label}: {label: string}) {
  return (
    <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-gray-400">
      {label} data will appear here
    </div>
  );
}

export const VehicleInsightsPanel: React.FC<VehicleInsightsPanelProps> =
  function ({isOpen = true, onToggle, vehicle}) {
    const [activeTab, setActiveTab] = useState<InsightTab>("trip");
    const d = DUMMY_DATA;

    if (!isOpen) return null;

    const vehicleNumber = vehicle?.name || d.vehicleNumber;
    const kmToday = vehicle?.distance || `${d.kmToday} km`;
    const currentSpeed = vehicle?.speed ?? d.currentSpeed;
    const statusLabel = vehicle?.currentStatus || "Stationary";
    const motionStatus =
      vehicle?.gpsTrackerStatus === GPSTrackerStatus.MOVING
        ? "Moving"
        : vehicle?.gpsTrackerStatus === GPSTrackerStatus.STATIONARY
          ? "Stationary"
          : "Offline";
    const motionStyle =
      motionStatus === "Moving"
        ? {bg: "bg-green-100", dot: "bg-green-500", text: "text-green-700"}
        : motionStatus === "Stationary"
          ? {bg: "bg-amber-100", dot: "bg-amber-500", text: "text-amber-500"}
          : {bg: "bg-red-100", dot: "bg-red-400", text: "text-red-500"};
    return (
      <div className="flex h-full w-[320px] flex-shrink-0 flex-col overflow-hidden  bg-white shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <SlArrowRight size={10} />
            <span className="font-semibold text-gray-800">Fleet Overview</span>
          </div>
          <div
            className="h-5 w-9 cursor-pointer rounded-full bg-green-500 p-0.5"
            onClick={onToggle}
          >
            <div className="ml-auto h-4 w-4 rounded-full bg-white shadow" />
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Vehicle card */}
          <div className="px-4 pt-4">
            <div className="flex items-center gap-3">
              {/* <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-xl bg-yellow-400 shadow-sm">
                <MdDirectionsBus size={16} color="white" />
              </div> */}
              <div className="flex flex-1 flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-xl bg-yellow-400 shadow-sm">
                      <MdDirectionsBus size={16} color="white" />
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      {vehicleNumber}
                    </span>
                  </div>
                  <div
                    className={`flex items-center gap-1 rounded-full px-2 py-0.5 ${motionStyle.bg}`}
                  >
                    <div
                      className={`h-1.5 w-1.5 rounded-full ${motionStyle.dot}`}
                    />
                    <span
                      className={`text-xs font-semibold ${motionStyle.text}`}
                    >
                      {motionStatus}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-1.5 rounded-xl bg-blue-50 px-3 py-2">
                    <Image
                      src="/icons/distance.svg"
                      alt="Distance"
                      radius="none"
                    />
                    <div>
                      <div className="text-sm font-bold text-blue-600">
                        {kmToday}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 rounded-xl bg-gray-100 px-3 py-2">
                    <Image
                      src="/icons/vehicleSpeed.svg"
                      alt="speed"
                      radius="none"
                    />
                    <div className="text-sm font-medium text-gray-700">
                      {currentSpeed} kph
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-3 border-b border-gray-100 px-1">
            <div className="flex w-full">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex flex-1 flex-col items-center gap-1 px-1 py-2.5 text-xs font-medium transition-colors ${
                    activeTab === tab.key
                      ? "border-b-2 border-blue-600 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {activeTab === "trip" && <TripTab vehicle={vehicle} />}
            {activeTab === "alerts" && <AlertsTab vehicle={vehicle} />}
            {activeTab === "attendance" && <AttendanceTab vehicle={vehicle} />}
            {activeTab === "fuel" && <FuelTab vehicle={vehicle} />}
            {activeTab === "driver" && <DriverTab vehicle={vehicle} />}
            {activeTab === "fleet" && <PlaceholderTab label="Fleet" />}
          </div>
        </div>
      </div>
    );
  };
